import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Phone, Mail, ExternalLink, MapPin, Clock, ArrowLeft, Star } from "lucide-react";

export default function DealDetail() {
  const { id } = useParams();
  const [deal, setDeal] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get(`/deals/${id}`).then(r => setDeal(r.data)).catch(e => setErr(e.response?.data?.detail || "Not found"));
  }, [id]);

  if (err) return <div className="max-w-3xl mx-auto px-6 py-24 text-center font-serif-display text-3xl">{String(err)}</div>;
  if (!deal) return <div className="max-w-3xl mx-auto px-6 py-24 text-center text-[#5F6C60]">Loading…</div>;

  const fallback = "https://images.pexels.com/photos/3997989/pexels-photo-3997989.jpeg?auto=compress&cs=tinysrgb&w=940";
  const savings = (deal.original_price - deal.deal_price).toFixed(2);
  const pct = Math.max(0, Math.round((1 - deal.deal_price/deal.original_price)*100));

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-12">
      <Link to="/browse" data-testid="back-to-browse" className="inline-flex items-center gap-2 text-sm text-[#5F6C60] hover:text-[#D97757] mb-8"><ArrowLeft className="w-4 h-4" /> Back to deals</Link>

      <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-10">
        <div>
          <div className="aspect-[4/3] rounded-3xl overflow-hidden bg-[#E8E2D6]">
            <img src={deal.image_url || fallback} alt={deal.title} className="w-full h-full object-cover" />
          </div>
        </div>

        <div className="space-y-6">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="bg-[#0F766E]/10 text-[#0F766E] text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full">{deal.category}</span>
            {pct > 0 && <span className="bg-[#D97757] text-white text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full">Save {pct}%</span>}
          </div>

          <h1 className="font-serif-display text-4xl lg:text-5xl tracking-tight leading-tight">{deal.title}</h1>

          <div className="flex items-center gap-5 text-sm text-[#5F6C60]">
            <span className="inline-flex items-center gap-1.5"><MapPin className="w-4 h-4" strokeWidth={1.5} /> {deal.city}, {deal.state} {deal.zip_code}</span>
            <span className="inline-flex items-center gap-1.5"><Clock className="w-4 h-4" strokeWidth={1.5} /> {deal.duration_minutes} min</span>
          </div>

          <div className="bg-white rounded-2xl border border-[#E8E2D6] p-8">
            <div className="flex items-baseline gap-3">
              <span className="font-serif-display text-5xl text-[#2C352D]">${deal.deal_price.toFixed(2)}</span>
              <span className="text-lg text-[#5F6C60] line-through">${deal.original_price.toFixed(2)}</span>
            </div>
            <div className="text-sm text-[#0F766E] mt-1">You save ${savings}</div>

            <div className="mt-6 text-xs font-bold uppercase tracking-[0.2em] text-[#5F6C60] mb-3">Redeem this deal</div>
            <div className="space-y-3">
              {deal.redemption_methods.includes("phone") && deal.contact_phone && (
                <a data-testid="redeem-phone" href={`tel:${deal.contact_phone}`} className="flex items-center justify-center gap-2 w-full bg-[#D97757] hover:bg-[#C26243] text-white rounded-full py-3 font-medium transition-colors">
                  <Phone className="w-4 h-4" /> Call {deal.contact_phone}
                </a>
              )}
              {deal.redemption_methods.includes("url") && deal.booking_url && (
                <a data-testid="redeem-url" href={deal.booking_url} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 w-full bg-[#0F766E] hover:bg-[#0C5F58] text-white rounded-full py-3 font-medium transition-colors">
                  <ExternalLink className="w-4 h-4" /> Book online
                </a>
              )}
              {deal.redemption_methods.includes("email") && deal.contact_email && (
                <a data-testid="redeem-email" href={`mailto:${deal.contact_email}?subject=Massage%20Savings%20Deal:%20${encodeURIComponent(deal.title)}`} className="flex items-center justify-center gap-2 w-full bg-white border border-[#2C352D] hover:bg-[#2C352D] hover:text-white text-[#2C352D] rounded-full py-3 font-medium transition-colors">
                  <Mail className="w-4 h-4" /> Email {deal.owner_name}
                </a>
              )}
            </div>
          </div>

          <div className="text-sm text-[#5F6C60]">
            Offered by <Link to={`/therapists/${deal.owner_id}`} data-testid="therapist-link" className="text-[#0F766E] font-medium hover:underline">{deal.owner_name}</Link>
          </div>
        </div>
      </div>

      <div className="mt-14 max-w-3xl">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#5F6C60] mb-3">About this deal</div>
        <p className="font-serif-display text-2xl leading-relaxed text-[#2C352D] whitespace-pre-line">{deal.description}</p>
      </div>

      <Reviews dealId={deal.id} />
    </div>
  );
}

function Reviews({ dealId }) {
  const [items, setItems] = React.useState([]);
  const [form, setForm] = React.useState({ rating: 5, body: "", customer_name: "", customer_email: "" });
  const [busy, setBusy] = React.useState(false);
  const load = React.useCallback(() => {
    api.get(`/deals/${dealId}/reviews`).then(r => setItems(r.data.items));
  }, [dealId]);
  React.useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/deals/${dealId}/reviews`, form);
      setForm({ rating: 5, body: "", customer_name: "", customer_email: "" });
      load();
    } catch (e2) {
      console.warn(e2);
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-16 max-w-3xl">
      <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#5F6C60] mb-3">Reviews</div>
      <div className="space-y-4 mb-8">
        {items.length === 0 && <p className="text-[#5F6C60]">No reviews yet — be the first.</p>}
        {items.map(r => (
          <div key={r.id} data-testid={`review-${r.id}`} className="bg-white border border-[#E8E2D6] rounded-2xl p-5">
            <div className="flex gap-0.5 text-[#D97757] mb-2">
              {[1,2,3,4,5].map(i => <Star key={i} className={`w-3.5 h-3.5 ${i <= r.rating ? "fill-current" : ""}`} strokeWidth={1.5}/>)}
            </div>
            <p>{r.body}</p>
            <div className="text-xs text-[#5F6C60] mt-2">— {r.customer_name}</div>
          </div>
        ))}
      </div>

      <form onSubmit={submit} className="bg-white border border-[#E8E2D6] rounded-2xl p-6 space-y-4">
        <div className="font-serif-display text-2xl">Leave a review</div>
        <div className="flex gap-1">
          {[1,2,3,4,5].map(i => (
            <button data-testid={`rating-${i}`} key={i} type="button" onClick={()=>setForm({...form, rating: i})}>
              <Star className={`w-7 h-7 ${i <= form.rating ? "text-[#D97757] fill-current" : "text-[#E8E2D6]"}`} strokeWidth={1.5}/>
            </button>
          ))}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <input data-testid="review-name" required placeholder="Your name" value={form.customer_name} onChange={e=>setForm({...form, customer_name: e.target.value})} className="h-11 px-3 bg-[#F9F7F3] border border-[#E8E2D6] rounded-xl"/>
          <input data-testid="review-email" required type="email" placeholder="Email (not published)" value={form.customer_email} onChange={e=>setForm({...form, customer_email: e.target.value})} className="h-11 px-3 bg-[#F9F7F3] border border-[#E8E2D6] rounded-xl"/>
        </div>
        <textarea data-testid="review-body" required rows={3} placeholder="How was your massage?" value={form.body} onChange={e=>setForm({...form, body: e.target.value})} className="w-full p-3 bg-[#F9F7F3] border border-[#E8E2D6] rounded-xl"/>
        <button data-testid="review-submit" disabled={busy} type="submit" className="rounded-full bg-[#D97757] hover:bg-[#C26243] text-white px-6 h-11 disabled:opacity-60">{busy?"Posting…":"Post review"}</button>
      </form>
    </div>
  );
}
